import type { UAExclusiveLimitValues } from "../server/lads-api/clients/lcc-openapi";
import type { ChannelThresholdFixed } from "./api/laboperator.types";
import type { GroupIdentifier } from "./mapper/common";

export type WorkplaceProperties = {
  [key: `can_id_${string}`]: {
    contains_devices?: number[];
    customName?: string;
  };
};

export type DeviceClass = "workplace" | "storage" | "device" | "monitoring";

// low = "nachtmodus aktiv" off = "inaktiv"
export type OperationMode = "none" | "off" | "low" | "normal" | "high";

export type DataPoint = {
  value: string | number | boolean;
  timestamp?: Date;
};

export type NumericDataPoint = {
  value: number;
  timestamp?: Date;
};

export type OperationDataPoint = {
  value: OperationMode;
  timestamp?: Date;
};

export type Channel<T = DataPoint> = {
  id: string;
  /** Canonical function path ("DeviceSet/<srcDev>/FunctionalUnitSet/<fu>/FunctionSet/<fn>") — THE channel identity; `id` is the canonical triple derived from it. */
  browsePath?: string;
  /** Device under whose view this channel was mapped (mount target). Differs from the browsePath device for mounted/external channels. */
  mountDeviceId?: string;
  name: string; // Name of the Channel
  suffix?: string;
  group?: GroupIdentifier; // Group identifier for sorting and organization
  unit?: string; // Unit of Channel values
  status: "on" | "off" | "orphaned" | "unknown"; // Indicates if the channel is connected to a source
  description?: string; // Description for the Channel
  interval?: number;
  current?: T;
  targetValue?: {
    channel: string;
    value?: DataPoint["value"];
    timestamp?: DataPoint["timestamp"];
  };
  hideEvalutaion?: boolean;
  isEnabled?: boolean; // OPC UA IsEnabled - false hides the channel from the UI
  valuePrecision?: number; // Number of decimal places for formatting
  historizing?: boolean; // OPC UA standard attribute - true if history is being recorded
  historizedVariables?: string[]; // List of variable names with historizing enabled (e.g., ["CurrentValue", "TargetValue"])
  controllable?: boolean; // true if function type contains "Control" (ControlFunction subtypes)
  controlFunctionState?: string; // ControlFunctionState CurrentState ("running"/"stopped"), live via UAControlFunctionState
  alarmMonitor?: UAExclusiveLimitValues; // OPC UA alarm limits (HighHighLimit, HighLimit, LowLimit, LowLowLimit)
  port?: string; // Physical connector identifier (e.g., "first", "left")
  enumStrings?: string[]; // Discrete value labels (e.g., ["Open", "Closed"])
};

export type SubClass =
  // fumehood
  | "fumehood"
  | "housing"
  // storage
  | "deep-freezer"
  | "refrigerator"
  | "acid-alkali-cabinet"
  | "gas-cabinet"
  | "safety-cabinet"
  | "battery-station"
  | "hplc-service-station"
  | "cool-safety-cabinet"
  | "classic-safety-cabinet"
  | "supreme-gas-cabinet"
  | "wall-mounted-cabinet"
  | "laboratory-cabinet"
  | "top-mounted-cabinet"
  | "under-bench-cabinet"
  | "incubator"
  // device
  | "unknown"
  | "sensor"
  | "sensorpanel"
  | "spider"
  | "sphere";

export type Class = Exclude<DeviceClass, "monitoring">;

export type Incident = {
  id: string;
  name: string; // channel name
  /** Function-level browse path ("DeviceSet/<dev>/FunctionalUnitSet/<fu>/FunctionSet/<fn>") — the matching key; `name` is a display label. Absent on legacy channel incidents. */
  browsePath?: string;
  createdAt: string;
  closedAt?: string | null;
  status?: "open" | "closed";
  /**
   * Whether the underlying OPC-UA alarm condition is currently raised.
   * Independent of `status`/`state`: an incident can have status="closed"
   * (operator confirmed) while the condition is still active.
   */
  active?: boolean;
  message: string; // derived from name and severity
  /**
   * Last operator comment recorded against this incident's episode —
   * surfaced from the LADS API. Empty when no ack/confirm/reset has been
   * issued yet.
   */
  comment?: string | null;
  /**
   * Parsed limit state (when the alarm comes from a Limit-style condition).
   * Drives a user-facing text instead of the raw OPC-UA message
   * "Condition is X and state is HighHigh".
   */
  limitState?: "HighHigh" | "High" | "Low" | "LowLow";
  /** Numeric value reported with the limit state, when parseable. */
  limitValue?: number;
  severity: "critical" | "warning";
  maxSeverity?: "critical" | "warning";
  code: number;
  help?: string;
  service: boolean;
  airflow: boolean;
  deviceId: string;
  /**
   * Operator raised an AuditConditionAcknowledge event during this episode
   * (sticky, history-aware). Drives the "Quittiert" tag. Independent of
   * `confirmed` and `reported`.
   */
  acknowledged?: boolean;
  /**
   * Operator raised an AuditConditionConfirm event during this episode
   * (sticky, history-aware). Drives the "Bestätigt" tag. Independent of
   * `acknowledged` and `reported`.
   */
  confirmed?: boolean;
  /**
   * Operator raised an AuditConditionReportedEventType (Essentim companion
   * spec, ns=…;i=15001) event during this episode (sticky, history-aware).
   * Drives the "Gemeldet" tag. Independent of `acknowledged` and `confirmed`.
   */
  reported?: boolean;
  /**
   * How many times the alarm flapped (active→normal while un-acked) within
   * this episode — Part-9 ConditionBranches folded into the incident. Only
   * set on real flap runs (≥ MIN_FLAP_RUN), absent otherwise.
   */
  flapCount?: number;
  /**
   * Whether this alarm needs an explicit Confirm to close (strict OPC-UA A&C:
   * New→Ack→Confirm) or whether Acknowledge is the terminal action (New→Ack).
   * A per-AlarmMonitor property (decided in the proxy installer). When `false`
   * the UI must NOT offer/require "Bestätigen" — Acknowledge alone closes it.
   * Absent ⇒ treat as strict (default).
   */
  strictAudited?: boolean;
  /**
   * Snapshot of the AlarmMonitor's four threshold properties at the moment
   * the alarm fired. Captured by the historian and stored in the alarm-event
   * payload, so the UI can render the limit that was breached even after
   * the operator changed the threshold later.
   */
  highHighLimit?: number;
  highLimit?: number;
  lowLimit?: number;
  lowLowLimit?: number;
  channelId?: string;
  threshold?: ChannelThresholdFixed;
};

export type DeviceChannels = {
  [key: string]: Channel;
};

export type ActiveProgram = {
  currentProgramTemplate?: string; // program name, e.g., "Peng -> Vent!"
  currentRuntime?: number; // seconds elapsed (excludes paused time)
  currentStepName?: string; // name of the current step
  currentStepNumber?: number; // current step index (1-based)
  currentStepRuntime?: number; // runtime of current step in seconds
  estimatedRuntime?: number; // total estimated seconds
  estimatedStepNumbers?: number; // total estimated steps
  estimatedStepRuntime?: number; // estimated runtime of current step
  currentPauseTime?: number; // accumulated pause time in seconds
  deviceProgramRunId?: string; // device-specific run identifier
};

export type Unit = {
  id: string;
  deviceId: string;
  /** Canonical FU browse path ("DeviceSet/<dev>/FunctionalUnitSet/<fu>") — the matching key; `name` is a display label. */
  browsePath: string;
  type: string;
  name: string;
  assetId?: string;
  state:
    | "aborted"
    | "aborting"
    | "clearing"
    | "stopped"
    | "running"
    | "stopping"
    | "on"
    | "off";
  functions: DeviceChannels;
  activeProgram?: ActiveProgram;
};

export type Device = {
  id: string; // Internal ID of the device (also used as browse-path leaf)
  type: string;
  state?: FumeHoodState;
  stateChannels: DeviceStateChannels;
  loId?: string;
  serial?: string; // Serial number of the device
  class: DeviceClass; // Device type for grouping
  subclass: SubClass;
  model?: string; // Device model
  manufacturer?: string; // Device manufacturer
  name?: string; // Human readable model name
  image: string; // The image location (static default)
  deviceTypeImage?: string[]; // OPC-UA DeviceTypeImage download links (lads/binary/{nodeId})
  location: {
    room?: string; // Room where the device is located in
    position?: string; // Position where the device is located within the Room
  };
  project?: string;
  statusChannels?: number[];
  incidents?: Incident[];
  incidentStates?: IncidentStates;
  actions?: {
    identify?: () => Promise<void>; // (set_ping command) blink the light of the fume hood
    customButton?: () => Promise<void>; // (press_panel_button) a generic button the functionality of which is defined by the driver
    light?: (on: boolean) => Promise<void>; // (set_light) turn the light on or off
    operation?: (mode: OperationMode) => Promise<void>; // (set_operation_status)
  };
  containedDeviceIds?: number[];
  updated_at?: string;
  workplaceProperties?: WorkplaceProperties;
  standard?: "laboperator" | "lads";
  parentId?: string;
  channels?: DeviceChannels;
};

export type FumehoodChannels = {
  angleFlap?: Channel;
  airflow?: Channel;
  operation?: Channel<OperationDataPoint>; // operation mode
  sliderVertical?: Channel;
  sliderHorizontal?: Channel;
  light?: Channel;
  temperature?: Channel;
  containerOne?: Channel;
  containerTwo?: Channel;
};

export type DeviceStateChannels = {
  /* calc the connnection state of the fumehood */
  // channel that indicates the connection state
  // state.connected = status ? "online" : "offline"
  status?: Channel;

  // channel that indicates a canbusCommunicationError
  // if (canCommError === 1) state.connected = error;
  // can_id_x_F_GC_MODUL_AUSFALL_ERR
  canCommError?: Channel;

  /* calc if fumehood is opened - ignored for now */
  // translate channel values to state.open
  // if (sashPos >=20mm || crossSlidePos >= 20mm) state.open = true
  // sashPos: Channel;
  // crossSlidePos: Channel;

  /* channels that should be translated into state message */
  // can_id_${canId}_status_info_I_BOOTUP
  bootupChannel?: Channel; // 99 - Gerät startet
  // can_id_${canId}_status_info_I_SERVICE_INFO
  serviceInfo?: Channel; // 18 - Service demnächst fällig
  // can_id_${canId}_status_info_I_LMB1_INFO
  lmb1Info?: Channel; // 61 - Lösemittelbehälter 1 ist voll
  // can_id_${canId}_status_info_I_LMB2_INFO
  lmb2Info?: Channel; // 62 - Lösemittelbehälter 2 ist voll
  // can_id_${canId}_status_info_I_TEMP_TLAST_INFO
  tempInfo?: Channel; // 21 - Erhöhte Temperatur
  // can_id_${canId}_status_info_I_WERK_INFO
  werkInfo?: Channel; // 101 - Steuerung in Werkszustand
};

export type FumehoodChannelsKeys = keyof FumehoodChannels;

export type FumeHoodStateMessage = {
  name: string;
  code: string;
  message: string;
  severity: "info";
};

export type DeviceState = {
  status?: "online" | "offline";
};

export type FumeHoodState = {
  status?: "online" | "offline" | "error" | "initialization";
  operation?: OperationMode;
  messages?: FumeHoodStateMessage[];
};

export type SupportedAutomations = "light" | "operation";

export const WEEK_DAYS = [
  "day1",
  "day2",
  "day3",
  "day4",
  "day5",
  "day6",
  "day7",
] as const;

export type WeekDays = (typeof WEEK_DAYS)[number];

export type TimeObject = {
  startTime?: string;
  endTime?: string;
};

export type ViewAutomation = {
  [key in SupportedAutomations]?: {
    [day in WeekDays]?: TimeObject;
  } & {
    enabled?: boolean;
    id?: string;
    deviceId?: string;
    name?: string;
  };
};

export type IncidentStates = {
  [id: string]: {
    reportedAt?: string;
    fixedAt?: string;
  };
};

export type DeviceWithAutomations = Device & {
  automations: ViewAutomation;
};

export type MonitorChannel = Channel<NumericDataPoint> & {
  past?: NumericDataPoint;
};
export type MonitorChannelsKeys = keyof MonitorChannels;
export type MonitorChannels = {
  temperature?: MonitorChannel;
  humidity?: MonitorChannel;
};

export type Monitor = Omit<Device, "channels"> & {
  channels: MonitorChannels;
};

export type RoomMonitoring = {
  name: string;
  path: string;
};

export type Room = {
  id: string;
  name: string;
  contactPerson?: string;
  contactData?: string;
  roomNumber?: string;
  deviceIds?: string[];
  airflowMin?: number;
  airflowMax?: number;
  monitoring?: RoomMonitoring[];
};
